
import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPaper, uploadPaperFile } from '@/lib/api';
import { cleanupOrphanedFile } from '@/lib/api/paper-storage';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { PaperAuthor } from '@/types/paper';
import { adminError } from '@/lib/utils/admin-logger';
import { convertArxivUrlToPdf } from '@/lib/utils/arxiv-utils';
import { supabase } from '@/lib/supabase';
import type { EmailChip } from '@/components/reviews/request/EmailChipInput';
import {
  PENDING_UPLOAD_SUBMIT_KEY,
  clearPendingIntent,
} from '@/lib/auth/pendingIntent';

// Sentinel value — must match the edge function's PENDING_ABSTRACT_SENTINEL
const PENDING_ABSTRACT_SENTINEL = 'Pending AI extraction';

/**
 * Derive a human-readable title from a filename.
 * "attention-is-all-you-need.pdf" → "Attention Is All You Need"
 */
export function titleFromFilename(filename: string): string {
  const withoutExt = filename.replace(/\.pdf$/i, '');
  const words = withoutExt
    .replace(/[-_]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2') // camelCase split
    .trim()
    .split(/\s+/);
  
  return words
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ') || 'Untitled Paper';
}

interface UseUploadSubmitProps {
  title: string;
  abstract: string;
  category: string;
  keywords: string[];
  additionalAuthors: PaperAuthor[];
  uploadMethod: 'file' | 'arxiv';
  file: File | null;
  arxivUrl: string;
  licenseCompliant?: boolean;
  visibility?: 'public' | 'private';
  requestReview?: boolean;
  selectedReviewers?: string[];
  publishedAt?: string;
  inviteChips?: EmailChip[];
  invitePersonalNote?: string;
  inviteWarranty?: boolean;
  onRequireAuth?: () => void;
  clearFormData?: () => void;
}

export const useUploadSubmit = ({
  title,
  abstract,
  category,
  keywords,
  additionalAuthors,
  uploadMethod,
  file,
  arxivUrl,
  licenseCompliant,
  visibility = 'public',
  requestReview = false,
  selectedReviewers = [],
  publishedAt,
  inviteChips = [],
  invitePersonalNote = '',
  inviteWarranty = false,
  onRequireAuth,
  clearFormData
}: UseUploadSubmitProps) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pendingSubmit, setPendingSubmit] = useState(false);

  const performSubmit = useCallback(async () => {
    if (!user) {
      return false;
    }
    
    const isPrivate = visibility === 'private';
    
    // For public papers, validate all fields strictly
    if (!isPrivate) {
      if (!title.trim() || !category.trim()) {
        toast.error('Please fill in all required fields');
        return false;
      }
      
      if (!abstract.trim()) {
        toast.error('Abstract is required');
        return false;
      }
      
      if (abstract.trim().length < 10) {
        toast.error('Abstract must be at least 10 characters long');
        return false;
      }
    }
    
    if (uploadMethod === 'file' && !file) {
      toast.error('Please select a PDF file to upload');
      return false;
    }
    
    // Check license compliance for arXiv papers
    if (uploadMethod === 'arxiv' && licenseCompliant === false) {
      toast.error('License Not Compliant', {
        description: 'This paper\'s license restricts commercial use and derivative works, which is not compatible with our platform\'s open sharing policy.',
        duration: 8000
      });
      return false;
    }
    
    setIsSubmitting(true);
    
    // For private papers, generate placeholder values if not provided by user
    let submitTitle = title.trim();
    let submitAbstract = abstract.trim();
    let submitCategory = category.trim();
    
    if (isPrivate) {
      if (!submitTitle && file) {
        submitTitle = titleFromFilename(file.name);
      } else if (!submitTitle) {
        submitTitle = 'Untitled Paper';
      }
      
      if (!submitAbstract || submitAbstract.length < 10) {
        submitAbstract = PENDING_ABSTRACT_SENTINEL;
      }
      
      if (!submitCategory) {
        submitCategory = 'other';
      }
    }
    
    // Prepare the paper data
    let fileUrl: string | undefined;
    let externalUrl: string | undefined;
    
    try {
      
      if (uploadMethod === 'file' && file) {
        // Upload the file directly during paper creation
        toast.loading('Uploading PDF file...', { id: 'file-upload' });
        const uploadedFileUrl = await uploadPaperFile(file);
        toast.dismiss('file-upload');
        
        if (!uploadedFileUrl) {
          toast.error('Failed to upload PDF file');
          return false;
        }
        
        fileUrl = uploadedFileUrl;
        toast.success('PDF uploaded successfully');
      } else if (uploadMethod === 'arxiv') {
        // Use the validated arXiv URL (keep original abstract URL for external_url)
        externalUrl = arxivUrl;
      }
      
      // Create the paper
      const result = await createPaper(
        submitTitle,
        submitAbstract,
        user.id,
        submitCategory,
        keywords,
        fileUrl,
        externalUrl,
        additionalAuthors,
        visibility,
        requestReview,
        selectedReviewers,
        publishedAt
      );
      
      if (result) {
        toast.success('Paper uploaded successfully');

        // Dispatch reviewer invitations (best-effort, non-blocking failure)
        const validInvites = (inviteChips ?? []).filter((c) => !c.warning).map((c) => c.email);
        if (
          requestReview &&
          visibility === 'public' &&
          inviteWarranty &&
          validInvites.length > 0
        ) {
          try {
            const { data: invData, error: invErr } = await supabase.functions.invoke(
              'manage-review-invitation',
              {
                body: {
                  action: 'create',
                  paperId: result.id,
                  emails: validInvites,
                  personalNote: invitePersonalNote?.trim() || undefined,
                  warrantyAccepted: true,
                },
              },
            );
            if (invErr) throw invErr;
            const created = invData?.created?.length ?? validInvites.length;
            toast.success(
              created === 1
                ? 'Review invitation sent'
                : `${created} review invitations sent`,
            );
          } catch (e) {
            adminError('Failed to send review invitations:', e);
            toast.error(
              'Paper uploaded, but we could not send your review invitations. You can send them from the paper page.',
            );
          }
        }

        clearFormData?.();
        clearPendingIntent(PENDING_UPLOAD_SUBMIT_KEY);
        navigate(`/upload/success/${result.id}`);
        return true;
      } else {
        toast.error('Failed to upload paper');
        return false;
      }
    } catch (error) {
      console.error('Error uploading paper:', error);
      adminError('Error uploading paper:', error);
      
      // Cleanup uploaded file if paper creation failed
      if (fileUrl && user?.id) {
        await cleanupOrphanedFile(fileUrl, user.id);
      }
      
      toast.error('An error occurred while uploading the paper');
      return false;
    } finally {
      setIsSubmitting(false);
      setPendingSubmit(false);
    }
  }, [user, title, abstract, category, keywords, additionalAuthors, uploadMethod, file, arxivUrl, licenseCompliant, visibility, requestReview, selectedReviewers, publishedAt, inviteChips, invitePersonalNote, inviteWarranty, navigate, clearFormData]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    // If user is not logged in, trigger auth modal instead of error
    if (!user) {
      setPendingSubmit(true);
      onRequireAuth?.();
      return;
    }
    
    await performSubmit();
  };

  // Called after successful authentication
  const handleAuthSuccess = useCallback(async () => {
    if (pendingSubmit) {
      // Small delay to ensure user state is updated
      setTimeout(() => {
        performSubmit();
      }, 100);
    }
  }, [pendingSubmit, performSubmit]);

  return {
    handleSubmit,
    isSubmitting,
    pendingSubmit,
    handleAuthSuccess,
    performSubmit
  };
};
